import { useCallback, useMemo, useRef, useState } from "react";
import type { Backend, TaskApi } from "../api/taskApi";
import {
  builtinShortcut,
  projectShortcut,
  sortWords,
  tagShortcut,
  type ListQuery,
  type Shortcut,
} from "../domain/views";
import { useNow, useSyncWhenBrowserReconnects } from "../state/environment";
import { useGlobalShortcuts } from "../state/shortcuts";
import { nextThemePreference, useSettings } from "../state/settings";
import { useWorkspace } from "../state/useWorkspace";
import { CommandPalette, type PaletteCommand } from "./CommandPalette";
import { Dialog } from "./Dialog";
import { SettingsDialog } from "./SettingsDialog";
import { DetailPane } from "./DetailPane";
import { FilterBar } from "./FilterBar";
import { ViewDefinition } from "./ViewDefinition";
import { QuickAdd } from "./QuickAdd";
import { Sidebar } from "./Sidebar";
import { TaskList } from "./TaskList";
import { ToastHost } from "./ToastHost";

interface WorkspaceScreenProps {
  api: TaskApi;
  backend: Backend;
  serverLabel: string;
  onSignedOut(): void;
}

type OpenDialog = "palette" | "settings" | "save-filter" | undefined;

export function WorkspaceScreen({ api, backend, serverLabel, onSignedOut }: WorkspaceScreenProps) {
  const { settings, setSettings } = useSettings();
  const [startupQuery] = useState(() => builtinShortcut(settings.startupShortcut).query);
  const workspace = useWorkspace(api, startupQuery);
  const { actions, tasks, selectedId, query } = workspace;
  const cycleTheme = useCallback(
    () => setSettings((current) => ({ ...current, theme: nextThemePreference(current.theme) })),
    [setSettings],
  );
  const now = useNow();
  useSyncWhenBrowserReconnects(() => void actions.syncNow());
  const [dialog, setDialog] = useState<OpenDialog>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const quickAddRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);

  const { selectShortcut: selectShortcutInWorkspace } = workspace;
  const selectShortcut = useCallback(
    (next: Shortcut) => {
      selectShortcutInWorkspace(next);
      setDrawerOpen(false);
    },
    [selectShortcutInWorkspace],
  );

  const moveSelection = (step: 1 | -1) => {
    if (tasks.length === 0) return;
    const current = tasks.findIndex((view) => view.task.id === selectedId);
    const next = Math.min(Math.max(current + step, 0), tasks.length - 1);
    const target = tasks[current < 0 ? 0 : next];
    if (target !== undefined) workspace.select(target.task.id);
  };

  const selectedView = tasks.find((candidate) => candidate.task.id === selectedId);

  useGlobalShortcuts({
    undo: () => void actions.undo(),
    redo: () => void actions.redo(),
    openPalette: () => setDialog("palette"),
    focusQuickAdd: () => quickAddRef.current?.focus(),
    focusFilter: () => filterRef.current?.focus(),
    selectNext: () => moveSelection(1),
    selectPrevious: () => moveSelection(-1),
    toggleSelected: () => {
      if (selectedView === undefined) return;
      void (selectedView.status === "Done" ? actions.reopen(selectedView) : actions.complete(selectedView));
    },
    closeDetail: () => workspace.select(undefined),
  });

  const signOut = async () => {
    await backend.signOut();
    onSignedOut();
  };

  const paletteCommands = useMemo<PaletteCommand[]>(() => {
    return [
      { id: "new", label: "New task", hint: "q", run: () => quickAddRef.current?.focus() },
      { id: "undo", label: "Undo", hint: "Ctrl+Z", run: () => void actions.undo() },
      { id: "redo", label: "Redo", hint: "Ctrl+Shift+Z", run: () => void actions.redo() },
      { id: "sync", label: "Sync now", run: () => void actions.syncNow() },
      { id: "theme", label: "Change theme", run: cycleTheme },
      { id: "save-filter", label: "Save current filter…", run: () => setDialog("save-filter") },
      { id: "settings", label: "Settings", run: () => setDialog("settings") },
      { id: "sign-out", label: "Sign out", run: () => void signOut() },
      ...workspace.shortcuts.map((target) => ({
        id: `go-${target.kind}-${target.key}`,
        label: target.kind === "tag" ? `Go to tag ${target.title}` : `Go to ${shortcutLabel(target)}`,
        run: () => selectShortcut(target),
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- signOut closes over stable props only
  }, [actions, cycleTheme, selectShortcut, workspace.shortcuts]);

  const { changeFilter } = workspace;
  const commandsForText = useCallback(
    (text: string): PaletteCommand[] => {
      const trimmed = text.trim();
      if (trimmed === "") return [];
      return [
        {
          id: "typed-filter",
          label: `Filter: ${trimmed}`,
          run: () => changeFilter(trimmed),
        },
        { id: "typed-add", label: `Add task: ${trimmed}`, run: () => void actions.addFromText(trimmed) },
      ];
    },
    [actions, changeFilter],
  );

  const listTitle = workspace.currentShortcuts[0]?.title ?? "Custom filter";
  const alreadySaved = workspace.currentShortcuts.some((shortcut) => shortcut.kind === "saved");

  return (
    <div className="shell">
      <Sidebar
        open={drawerOpen}
        query={query}
        projects={workspace.projects}
        savedFilters={workspace.savedFilters}
        tags={workspace.tags}
        inbox={workspace.inbox}
        syncStatus={workspace.syncStatus}
        theme={settings.theme}
        onSelectShortcut={selectShortcut}
        onSyncNow={() => void actions.syncNow()}
        onCycleTheme={cycleTheme}
        onOpenSettings={() => setDialog("settings")}
      />
      <div className={`scrim${drawerOpen ? " open" : ""}`} onClick={() => setDrawerOpen(false)} />

      <main className="main">
        <header className="topbar">
          <button
            type="button"
            className="icon-button menu-button"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
          >
            ☰
          </button>
          <div className="filter-bar">
            <FilterBar
              activeFilter={query.filter}
              inputRef={filterRef}
              onApply={workspace.changeFilter}
            />
          </div>
          <span
            className="icon-button"
            role="img"
            aria-label="About filters"
            title="In a filter, ready and next are shorthand for status:pending. An empty filter shows all open tasks (status:pending or status:waiting or status:blocked)."
          >
            ?
          </span>
          <span className="topbar-spacer" />
          <button type="button" className="icon-button" aria-label="Undo" title="Undo (Ctrl+Z)" onClick={() => void actions.undo()}>
            ↶
          </button>
          <button type="button" className="icon-button" aria-label="Redo" title="Redo (Ctrl+Shift+Z)" onClick={() => void actions.redo()}>
            ↷
          </button>
          <button type="button" className="icon-button" aria-label="Open command palette" title="Command palette (Ctrl+K)" onClick={() => setDialog("palette")}>
            ⌘
          </button>
        </header>

        <div className="list-scroll">
          <div className="list-inner">
            <div className="view-header">
              <h1 className="view-title">{listTitle}</h1>
              <span className="view-meta">{tasks.length} {tasks.length === 1 ? "task" : "tasks"}</span>
            </div>
            <ViewDefinition
              sort={query.sort}
              grouping={workspace.grouping}
              onChangeSort={workspace.changeSort}
              onSaveAsFilter={alreadySaved ? undefined : () => setDialog("save-filter")}
            />

            <QuickAdd
              inputRef={quickAddRef}
              defaultProject={workspace.currentShortcuts[0]?.quickAddDefaults.project}
              onSubmit={actions.addFromText}
            />

            {workspace.queryProblem !== undefined && (
              <p className="problem" role="alert">
                {workspace.queryProblem}
              </p>
            )}
            {workspace.loadState === "loading" && <p className="empty">Loading…</p>}
            {workspace.loadState === "ready" && tasks.length === 0 && workspace.queryProblem === undefined && (
              <p className="empty">Nothing here. Add a task above.</p>
            )}
            <TaskList
              tasks={tasks}
              grouping={workspace.grouping}
              selectedId={selectedId}
              now={now}
              onSelect={workspace.select}
              onOpenTag={(tag) => selectShortcut(tagShortcut(tag))}
              onOpenProject={(path) => selectShortcut(projectShortcut(path))}
              onToggleComplete={(task) => void (task.status === "Done" ? actions.reopen(task) : actions.complete(task))}
              onRestore={(task) => void actions.restore(task)}
            />
          </div>
        </div>
        <ToastHost toasts={workspace.toasts.toasts} dismiss={workspace.toasts.dismiss} />
      </main>

      {selectedId !== undefined && (
        <DetailPane
          key={selectedId}
          workspace={workspace}
          taskId={selectedId}
          now={now}
          onClose={() => workspace.select(undefined)}
          onOpenTag={(tag) => selectShortcut(tagShortcut(tag))}
        />
      )}

      {dialog === "palette" && (
        <CommandPalette commands={paletteCommands} commandsForText={commandsForText} onClose={() => setDialog(undefined)} />
      )}
      {dialog === "settings" && (
        <SettingsDialog
          settings={settings}
          serverLabel={serverLabel}
          onChange={setSettings}
          onSignOut={() => void signOut()}
          onClose={() => setDialog(undefined)}
        />
      )}
      {dialog === "save-filter" && (
        <SaveFilterDialog
          query={query}
          onClose={() => setDialog(undefined)}
          onSave={(name) => {
            setDialog(undefined);
            void actions.saveFilter(name, query);
          }}
        />
      )}
    </div>
  );
}

function shortcutLabel(shortcut: Shortcut): string {
  return shortcut.kind === "project" ? shortcut.key : shortcut.title;
}

function SaveFilterDialog({
  query,
  onSave,
  onClose,
}: {
  query: ListQuery;
  onSave(name: string): void;
  onClose(): void;
}) {
  const [name, setName] = useState("");
  return (
    <Dialog title="Save filter" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() !== "") onSave(name.trim());
        }}
      >
        <p className="field-hint">
          {query.filter}, sorted by {sortWords(query.sort)}
        </p>
        <div className="field">
          <label htmlFor="filter-name">Name</label>
          <input
            id="filter-name"
            className="text-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <p className="field-hint">One word: no spaces, quotes or colons. Use it in any filter to run this one.</p>
        </div>
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button primary">
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
